import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { QRCodeSVG } from 'qrcode.react';
import { useNotification } from '../../contexts/NotificationContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Users, Eye, MousePointerClick, Target } from 'lucide-react';
import NotificationBell from '../shared/NotificationBell';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

const CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .pm-wrap{font-family:${FONT_UI};}
  .pm-title{font-family:${FONT};font-weight:300;letter-spacing:-0.01em;color:#0A0A0A;}
  .pm-subtitle{font-family:${FONT_UI};color:#6B6B6B;}
  .pm-greeting{font-family:${FONT_UI};font-size:13px;color:#9A9A9A;font-weight:500;letter-spacing:-0.005em;margin:0 0 2px;}
  .pm-header{display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:12px;}
  .pm-banner{border-radius:10px;padding:14px 16px;font-family:${FONT_UI};font-size:13.5px;border:1px solid;}
  .pm-banner--error{background:rgba(220,38,38,0.06);border-color:rgba(220,38,38,0.25);color:#DC2626;}
  .pm-banner--ok{background:rgba(22,163,74,0.06);border-color:rgba(22,163,74,0.25);color:#16A34A;}
  .pm-btn{font-size:13.5px;font-weight:600;padding:10px 18px;border-radius:9px;cursor:pointer;text-align:center;letter-spacing:-0.01em;transition:background .15s,border-color .15s,opacity .15s;display:inline-flex;align-items:center;justify-content:center;gap:7px;border:none;font-family:${FONT_UI};}
  .pm-btn-primary{background:#0A0A0A;color:#fff;}
  .pm-btn-primary:hover{background:#1a1a1a;}
  .pm-btn:disabled{opacity:.5;cursor:not-allowed;}
  .pm-table-card{background:#fff;border:1px solid #E5E5E5;border-radius:14px;}
  .pm-table{width:100%;border-collapse:collapse;}
  .pm-table thead th{text-align:left;font-size:11px;font-weight:600;color:#6B6B6B;text-transform:uppercase;letter-spacing:.04em;padding:12px 18px;background:#FAFAFA;border-bottom:1px solid #E5E5E5;font-family:${FONT_UI};}
  .pm-table thead th:first-child{border-top-left-radius:14px;}
  .pm-table thead th:last-child{border-top-right-radius:14px;}
  .pm-table tbody td{padding:16px 18px;border-bottom:1px solid #F0F0F0;font-size:13.5px;color:#0A0A0A;vertical-align:middle;font-family:${FONT_UI};}
  .pm-table tbody tr:last-child td{border-bottom:none;}
  .pm-row{cursor:pointer;transition:background .15s;}
  .pm-row:hover{background:#F7F7F7;}
  .pm-row.selected{background:#FAFAFA;box-shadow:inset 3px 0 0 0 #0A0A0A;}
  .pm-profile-cell{display:flex;align-items:center;gap:12px;}
  .pm-avatar-sm{width:38px;height:38px;border-radius:50%;object-fit:cover;flex-shrink:0;}
  .pm-avatar-sm-fallback{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;flex-shrink:0;font-family:${FONT_UI};}
  .pm-p-name{font-size:13.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .pm-p-role{font-size:12px;color:#9A9A9A;margin-top:1px;}
  .pm-url{font-family:monospace;color:#9A9A9A;font-size:12px;}
  .pm-visits{font-weight:600;color:#0A0A0A;}
  .pm-date{color:#9A9A9A;font-size:12.5px;}
  .pm-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:980px;font-size:11.5px;font-weight:600;}
  .pm-badge--active{background:rgba(22,163,74,0.1);color:#16A34A;}
  .pm-badge--inactive{background:rgba(220,38,38,0.1);color:#DC2626;}
  .pm-badge-dot{width:5px;height:5px;border-radius:50%;background:currentColor;flex-shrink:0;}
  .pm-actions-cell{display:flex;gap:6px;justify-content:flex-end;}
  .pm-icon-btn{width:30px;height:30px;border-radius:8px;border:1px solid #E5E5E5;background:#fff;display:flex;align-items:center;justify-content:center;color:#525252;cursor:pointer;transition:background .15s,border-color .15s,color .15s;flex-shrink:0;}
  .pm-icon-btn:hover{background:#FAFAFA;border-color:#D4D4D4;color:#0A0A0A;}
  .pm-icon-btn.warn:hover{background:rgba(217,119,6,0.08);border-color:rgba(217,119,6,0.3);color:#D97706;}
  .pm-icon-btn.success:hover{background:rgba(22,163,74,0.08);border-color:rgba(22,163,74,0.3);color:#16A34A;}
  .pm-icon-btn:disabled{opacity:.4;cursor:not-allowed;}
  .pm-icon-btn.star-active{background:#0A0A0A;border-color:#0A0A0A;color:#fff;cursor:default;opacity:1;}
  .pm-icon-btn.star-active:hover{background:#0A0A0A;border-color:#0A0A0A;color:#fff;}
  .pm-icon-btn--primary{background:#0A0A0A;border-color:#0A0A0A;color:#fff;}
  .pm-icon-btn--primary:hover{background:#1a1a1a;border-color:#1a1a1a;color:#fff;}
  .pm-kebab-wrap{position:relative;}
  .pm-kebab-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1px solid #E5E5E5;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.12);padding:5px;min-width:190px;z-index:60;display:flex;flex-direction:column;gap:1px;}
  .pm-kebab-menu--up{top:auto;bottom:calc(100% + 6px);}
  .pm-kebab-item{display:flex;align-items:center;gap:9px;width:100%;text-align:left;padding:9px 10px;border-radius:7px;border:none;background:none;color:#0A0A0A;font-size:12.5px;font-weight:500;cursor:pointer;font-family:${FONT_UI};transition:background .12s;}
  .pm-kebab-item:hover{background:#FAFAFA;}
  .pm-kebab-item:disabled{opacity:.45;cursor:not-allowed;}
  .pm-kebab-item.warn:hover{background:rgba(217,119,6,0.08);color:#D97706;}
  .pm-kebab-item.success:hover{background:rgba(22,163,74,0.08);color:#16A34A;}
  .pm-kebab-item.danger{color:#DC2626;}
  .pm-kebab-item.danger:hover{background:rgba(220,38,38,0.08);color:#DC2626;}
  .pm-p-name-row{display:flex;align-items:center;gap:7px;}
  .pm-default-badge{display:inline-flex;align-items:center;font-size:10px;font-weight:600;color:#0A0A0A;background:#F0F0F0;padding:2px 8px;border-radius:980px;letter-spacing:0.01em;white-space:nowrap;}
  .pm-empty{padding:64px 24px;text-align:center;}
  .pm-empty-icon{width:44px;height:44px;border-radius:50%;background:#F5F5F5;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;color:#A3A3A3;}
  .pm-empty-title{font-size:14px;font-weight:600;color:#0A0A0A;margin-bottom:4px;}
  .pm-empty-sub{font-size:12.5px;color:#9A9A9A;}
  .pm-modal-label{text-align:center;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#6B6B6B;margin-bottom:18px;font-family:${FONT_UI};}
  .pm-qr-box{background:#fff;border:1px solid #E5E5E5;border-radius:16px;padding:20px;display:flex;flex-direction:column;align-items:center;margin-bottom:18px;}
  .pm-handle{font-size:19px;font-weight:700;color:#0A0A0A;letter-spacing:-0.01em;font-family:${FONT_UI};}
  .pm-handle-sub{font-size:12.5px;color:#6B6B6B;margin-top:2px;font-family:${FONT_UI};}
  .pm-modal-actions{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .pm-modal-btn{padding:12px 8px;border-radius:12px;font-weight:600;font-size:12px;display:flex;flex-direction:column;align-items:center;gap:6px;border:1px solid #E5E5E5;background:#fff;color:#0A0A0A;cursor:pointer;transition:background .15s,border-color .15s;font-family:${FONT_UI};}
  .pm-modal-btn:hover{background:#FAFAFA;border-color:#D4D4D4;}
  .pm-modal-btn:disabled{opacity:.5;cursor:not-allowed;}
  .pm-modal-btn--ok{background:rgba(22,163,74,0.08);border-color:rgba(22,163,74,0.25);color:#16A34A;}
  .pm-modal-btn--primary{background:#0A0A0A;border-color:#0A0A0A;color:#fff;}
  .pm-modal-btn--primary:hover{background:#1a1a1a;border-color:#1a1a1a;}
  .pm-slug-warning{background:rgba(217,119,6,0.06);border:1px solid rgba(217,119,6,0.22);border-radius:10px;padding:12px 14px;font-size:12.5px;line-height:1.5;color:#8a5a00;margin-bottom:18px;font-family:${FONT_UI};text-align:left;}
  .pm-form-label{display:block;font-size:12px;font-weight:600;color:#6B6B6B;margin-bottom:6px;font-family:${FONT_UI};text-align:left;}
  .pm-slug-input-row{display:flex;align-items:center;border:1px solid rgba(0,0,0,0.12);border-radius:10px;overflow:hidden;background:#fff;transition:border-color .15s,box-shadow .15s;}
  .pm-slug-input-row:focus-within{border-color:#0071e3;box-shadow:0 0 0 3px rgba(0,113,227,0.12);}
  .pm-slug-prefix{padding:11px 0 11px 12px;font-size:12px;color:#9A9A9A;font-family:monospace;white-space:nowrap;}
  .pm-slug-input{flex:1;min-width:0;border:none;outline:none;padding:11px 12px 11px 2px;font-size:13.5px;color:#0A0A0A;font-family:monospace;background:transparent;}
  .pm-slug-error{font-size:12px;color:#DC2626;margin-top:8px;font-family:${FONT_UI};text-align:left;}
  .pm-wallet-divider{border:none;border-top:1px solid #F0F0F0;margin:18px 0 16px;}
  .pm-wallet-row{display:flex;align-items:center;gap:12px;}
  .pm-wallet-copy{flex:1;min-width:0;}
  .pm-wallet-title{font-size:12.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.005em;margin-bottom:2px;}
  .pm-wallet-sub{font-size:11.5px;color:#9A9A9A;line-height:1.4;}
  .pm-wallet-badge-link{flex-shrink:0;display:inline-flex;line-height:0;}
  .pm-wallet-badge-img{height:40px;width:auto;max-width:100%;display:block;}
  .pm-wallet-soon{flex-shrink:0;display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border-radius:980px;background:#F5F5F5;color:#9A9A9A;font-size:11px;font-weight:600;white-space:nowrap;font-family:${FONT_UI};}

  .pm-stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:14px;margin-bottom:28px;}
  .pm-stat-card{background:#fff;border:1px solid #E5E5E5;border-radius:12px;padding:16px 18px;box-shadow:0 1px 2px rgba(0,0,0,0.04);transition:box-shadow .2s ease,border-color .2s ease;}
  .pm-stat-card:hover{box-shadow:0 2px 10px rgba(0,0,0,0.07);border-color:#D4D4D4;}
  .pm-stat-icon{width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;margin-bottom:12px;}
  .pm-stat-value{font-size:26px;font-weight:700;color:#0A0A0A;letter-spacing:-0.02em;line-height:1;font-family:${FONT_UI};}
  .pm-stat-value-in{animation:pm-stat-fade-in .35s ease both;}
  @keyframes pm-stat-fade-in{from{opacity:0;transform:translateY(2px);}to{opacity:1;transform:translateY(0);}}
  .pm-stat-label{font-size:12.5px;color:#6B6B6B;font-weight:500;margin-top:5px;letter-spacing:-0.005em;font-family:${FONT_UI};}

  .pm-mobile-list{display:none;}
  .pm-mobile-card{background:#fff;border:1px solid #F0F0F0;border-radius:12px;padding:12px;margin-bottom:8px;cursor:pointer;transition:background .12s;}
  .pm-mobile-card:last-child{margin-bottom:0;}
  .pm-mobile-card.selected{background:#FAFAFA;box-shadow:inset 3px 0 0 0 #0A0A0A;}
  .pm-mobile-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px;}
  .pm-mobile-card-id{display:flex;align-items:center;gap:9px;min-width:0;}
  .pm-mobile-card-name{font-size:12.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .pm-mobile-card-role{font-size:10.5px;color:#9A9A9A;margin-top:1px;}
  .pm-mobile-card-meta{display:flex;align-items:center;gap:5px;font-size:10.5px;color:#9A9A9A;margin-bottom:10px;flex-wrap:wrap;font-family:monospace;}

  @media (max-width: 768px) {
    .pm-wallet-row{flex-direction:column;align-items:flex-start;gap:8px;}
    .pm-wallet-badge-link,.pm-wallet-soon{align-self:flex-start;}
    .pm-stat-grid{grid-template-columns:repeat(2,1fr);gap:9px;margin-bottom:20px;}
    .pm-stat-card{padding:12px 13px;}
    .pm-stat-icon{width:28px;height:28px;border-radius:7px;margin-bottom:8px;}
    .pm-stat-value{font-size:19px;}
    .pm-stat-label{font-size:10.5px;margin-top:3px;}
    .pm-header{gap:8px;}
    .pm-banner{padding:9px 11px;font-size:10.5px;}
    .pm-btn{font-size:10.5px;padding:9px 12px;gap:4px;min-height:34px;}
    .pm-date{font-size:10.5px;}
    .pm-default-badge{font-size:10.5px;padding:2px 6px;}
    .pm-empty{padding:41px 15px;}
    .pm-actions-cell{gap:4px;}
    .pm-avatar-sm{width:27px;height:27px;}
    .pm-avatar-sm-fallback{width:27px;height:27px;font-size:12px;}
    .pm-badge{padding:2px 7px;font-size:10.5px;gap:3px;flex-shrink:0;}
    .pm-badge-dot{width:4px;height:4px;}
    .pm-wrap{font-size:11px;}
    .pm-icon-btn{width:26px;height:26px;}
    .pm-p-name{font-size:11.5px;}
    .pm-p-role{font-size:10.5px;}
    .pm-table{display:none;}
    .pm-mobile-list{display:block;padding:8px;padding-bottom:calc(50px + 32px + env(safe-area-inset-bottom));}
    .pm-url{font-size:10.5px;}
  }
`;

const AVATAR_COLORS = ['#2563EB', '#7C3AED', '#D97706', '#16A34A', '#DB2777', '#0891B2'];
const getInitials = (profile) => {
  const a = (profile.name || '').trim()[0] || '';
  const b = (profile.lastname || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
};
const getAvatarColor = (id) => {
  let hash = 0;
  const str = String(id || '');
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ProfilesManager = ({ profiles, currentProfile, setCurrentProfile, createNewProfile, refreshProfiles, user, setActiveTab }) => {
  const { confirm, toast } = useNotification();
  const { hasFeature, openUpgrade } = usePlan();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [togglingStatus, setTogglingStatus] = useState(null); // profile id being toggled
  const [settingDefault, setSettingDefault] = useState(null); // profile id being set as default
  const [deletingProfile, setDeletingProfile] = useState(null); // profile id being deleted
  const [shareModalProfile, setShareModalProfile] = useState(null); // profile being shared
  const [copySuccess, setCopySuccess] = useState(false);
  const [slugModalProfile, setSlugModalProfile] = useState(null); // profile whose link is being changed
  const [slugInput, setSlugInput] = useState('');
  const [slugError, setSlugError] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [walletConfigured, setWalletConfigured] = useState(false);
  const [walletBadgeError, setWalletBadgeError] = useState(false);
  const [googleWalletConfigured, setGoogleWalletConfigured] = useState(false);
  const [googleWalletBadgeError, setGoogleWalletBadgeError] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null); // profile id whose kebab menu is open
  // Table vs. mobile-card list is decided in JS (not just CSS display:none),
  // so the two views can never both mount into the DOM at once.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Público, sin auth: solo nos dice si ya se cargaron credenciales de Apple
  // en el backend, para mostrar el badge real o el estado "Próximamente".
  useEffect(() => {
    axios.get(`${API}/apple-wallet/status`)
      .then((res) => setWalletConfigured(!!res.data?.configured))
      .catch(() => setWalletConfigured(false));
    axios.get(`${API}/google-wallet/status`)
      .then((res) => setGoogleWalletConfigured(!!res.data?.configured))
      .catch(() => setGoogleWalletConfigured(false));
  }, []);
  // Quiet cross-profile overview strip (Inicio) — each stat is independently
  // optional so Free-plan accounts (no analytics/leads feature) still see
  // "Perfiles activos" without triggering the 402 upgrade-modal interceptor.
  const [homeStats, setHomeStats] = useState({ totalVisits: null, totalClicks: null, leadsLast30: null });
  // Per-profile real-time visit counts (from the same analytics events the
  // rest of the app trusts) — keyed by profile_id, used to override the
  // "VISITAS" column instead of the profile document's own `visits` field,
  // which is a separately-maintained counter that can drift out of sync.
  const [profileVisits, setProfileVisits] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (hasFeature('analytics')) {
      axios.get(`${API}/analytics/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          const summary = res.data || [];
          const totalVisits = summary.reduce((sum, p) => sum + (p.total_visits || 0), 0);
          const totalClicks = summary.reduce((sum, p) => sum + (p.total_clicks || 0), 0);
          setHomeStats((prev) => ({ ...prev, totalVisits, totalClicks }));
          const visitsMap = {};
          summary.forEach((p) => { visitsMap[p.profile_id] = p.total_visits || 0; });
          setProfileVisits(visitsMap);
        })
        .catch(() => {});
    }

    if (hasFeature('leads')) {
      axios.get(`${API}/leads/me/summary`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          setHomeStats((prev) => ({ ...prev, leadsLast30: res.data?.leads_last_30_days ?? 0 }));
        })
        .catch(() => {});
    }
  }, [hasFeature]);

  useEffect(() => {
    if (openMenuId === null) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('cn:dashboard-menu', { detail: { open: openMenuId !== null } }));
  }, [openMenuId]);

  const handleCreateProfile = async () => {
    if (!(await confirm(t('profilesManager.confirm.createProfile', '¿Estás seguro de que deseas crear un nuevo perfil?'), { confirmLabel: t('profilesManager.createProfile', 'Crear perfil') }))) {
      return;
    }

    setCreating(true);
    setMessage('');

    const result = await createNewProfile();

    if (result.success) {
      setMessage(t('profilesManager.msg.created', 'Nuevo perfil creado exitosamente'));
      setTimeout(() => setMessage(''), 3000);
    } else {
      // Show more detailed error message
      const errorMsg = result.error || t('profilesManager.msg.unknownCreateError', 'Error desconocido al crear perfil');
      setMessage(errorMsg.includes('Maximum') ?
        t('profilesManager.msg.maxProfilesReached', 'Has alcanzado el límite máximo de perfiles. Contacta al administrador si necesitas más.') :
        `${t('profilesManager.error', 'Error')}: ${errorMsg}`
      );
      setTimeout(() => setMessage(''), 5000);
    }

    setCreating(false);
  };

  const setProfileActive = async (profileId, isActive) => {
    const token = localStorage.getItem('token');
    await axios.put(
      `${API}/profiles/${profileId}/status`,
      { is_active: isActive },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await refreshProfiles?.();
  };

  // Both directions of this toggle are instantly reversible (it's a single
  // boolean flag), so a blocking confirm() is unnecessary friction — the
  // action fires immediately and, when deactivating, an undo toast offers
  // a one-click way back instead of asking "are you sure?" up front.
  const handleToggleProfileStatus = async (profileId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    const nextActive = !currentStatus;

    setTogglingStatus(profileId);
    setMessage('');

    try {
      await setProfileActive(profileId, nextActive);

      if (nextActive) {
        setMessage(t('profilesManager.msg.activated', 'Perfil activado exitosamente'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        toast.undo(t('profilesManager.toast.deactivated', 'Perfil desactivado. No será visible públicamente.'), async () => {
          try {
            await setProfileActive(profileId, true);
            toast.success(t('profilesManager.toast.reactivated', 'Perfil reactivado'));
          } catch (error) {
            toast.error(t('profilesManager.toast.undoError', 'No se pudo deshacer. Reactiva el perfil manualmente.'));
          }
        });
      }
    } catch (error) {
      console.error(`Error al ${action} perfil:`, error);
      const actionErrorFallback = currentStatus ? t('profilesManager.msg.deactivateError', 'Error al desactivar el perfil') : t('profilesManager.msg.activateError', 'Error al activar el perfil');
      const errorMsg = error.response?.data?.detail || actionErrorFallback;
      setMessage(`${t('profilesManager.error', 'Error')}: ${errorMsg}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleSetDefault = async (profileId) => {
    setSettingDefault(profileId);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profiles/${profileId}/set-default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage(t('profilesManager.msg.defaultUpdated', 'Perfil principal actualizado exitosamente'));
      setTimeout(() => setMessage(''), 3000);

      await refreshProfiles?.();
    } catch (error) {
      console.error('Error al marcar perfil principal:', error);
      const errorMsg = error.response?.data?.detail || t('profilesManager.msg.defaultError', 'Error al marcar perfil principal');
      setMessage(`${t('profilesManager.error', 'Error')}: ${errorMsg}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSettingDefault(null);
    }
  };

  const handleDeleteProfile = async (profile) => {
    const lastOne = profiles.length === 1;
    const warning = lastOne
      ? ` ${t('profilesManager.confirm.onlyProfileWarning', 'Es tu único perfil: después de eliminarlo no tendrás ningún perfil activo.')}`
      : '';
    const ok = await confirm(
      t('profilesManager.confirm.deleteProfile', `¿Eliminar el perfil "${profile.name || 'Sin nombre'}"? Se perderán para siempre sus leads, analíticas y estadísticas guardadas. Esta acción no se puede deshacer.`).replace('{name}', profile.name || t('profilesManager.noName', 'Sin nombre')) + warning,
      { title: t('profilesManager.deleteProfile', 'Eliminar perfil'), confirmLabel: t('profilesManager.deleteProfile', 'Eliminar perfil'), cancelLabel: t('profilesManager.cancel', 'Cancelar') }
    );
    if (!ok) return;

    setDeletingProfile(profile.id);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profiles/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('profilesManager.toast.profileDeleted', 'Perfil eliminado'));
      if (currentProfile?.id === profile.id) {
        const remaining = profiles.filter((p) => p.id !== profile.id);
        setCurrentProfile(remaining[0] || null);
      }
      await refreshProfiles?.();
    } catch (error) {
      console.error('Error al eliminar perfil:', error);
      toast.error(error.response?.data?.detail || t('profilesManager.toast.deleteError', 'No se pudo eliminar el perfil'));
    } finally {
      setDeletingProfile(null);
    }
  };

  const handleShareProfile = (profile) => {
    setShareModalProfile(profile);
    setCopySuccess(false);
  };

  const handleCopyLink = () => {
    const profileUrl = `${window.location.origin}/pages/${encodeURIComponent(shareModalProfile.slug)}`;
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const closeShareModal = () => {
    setShareModalProfile(null);
    setCopySuccess(false);
  };

  const handleOpenSlugModal = (profile) => {
    setSlugModalProfile(profile);
    setSlugInput(profile.slug || '');
    setSlugError('');
  };

  const closeSlugModal = () => {
    if (savingSlug) return;
    setSlugModalProfile(null);
    setSlugInput('');
    setSlugError('');
  };

  const handleSaveSlug = async () => {
    if (!slugModalProfile) return;
    const trimmed = slugInput.trim();
    if (!trimmed) {
      setSlugError(t('profilesManager.slug.enterValid', 'Ingresa un link válido'));
      return;
    }

    setSavingSlug(true);
    setSlugError('');

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profiles/${slugModalProfile.id}/slug`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, params: { new_slug: trimmed } }
      );
      toast.success(t('profilesManager.slug.updated', 'Link actualizado correctamente'));
      setSlugModalProfile(null);
      setSlugInput('');
      await refreshProfiles?.();
    } catch (error) {
      setSlugError(error.response?.data?.detail || t('profilesManager.slug.updateError', 'No se pudo actualizar el link'));
    } finally {
      setSavingSlug(false);
    }
  };

  const getUserMaxProfiles = () => {
    // Get the real max_profiles from user data
    return user?.max_profiles || 1;
  };

  const canCreateMore = profiles.length < getUserMaxProfiles();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('profilesManager.greeting.morning', 'Buenos días');
    if (hour < 19) return t('profilesManager.greeting.afternoon', 'Buenas tardes');
    return t('profilesManager.greeting.evening', 'Buenas noches');
  };

  const ProfileActions = ({ profile, isActive, openUp }) => (
    <div className="pm-actions-cell" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => window.open(`/pages/${encodeURIComponent(profile.slug)}`, '_blank')}
        className="pm-icon-btn pm-icon-btn--primary"
        title={t('profilesManager.viewProfile', 'Ver perfil')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      <div className="pm-kebab-wrap">
        <button
          onClick={() => setOpenMenuId(openMenuId === profile.id ? null : profile.id)}
          className="pm-icon-btn"
          title={t('profilesManager.moreActions', 'Más acciones')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
        {openMenuId === profile.id && (
          <div className={`pm-kebab-menu${openUp ? ' pm-kebab-menu--up' : ''}`}>
            <button
              onClick={() => { setOpenMenuId(null); !profile.is_default && handleSetDefault(profile.id); }}
              disabled={profile.is_default || settingDefault === profile.id}
              className="pm-kebab-item"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={profile.is_default ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {profile.is_default ? t('profilesManager.mainProfile', 'Perfil principal') : t('profilesManager.markAsMain', 'Marcar como principal')}
            </button>
            <button
              onClick={() => { setOpenMenuId(null); handleShareProfile(profile); }}
              className="pm-kebab-item"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {t('profilesManager.share', 'Compartir')}
            </button>
            <button
              onClick={() => { setOpenMenuId(null); handleOpenSlugModal(profile); }}
              className="pm-kebab-item"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
              </svg>
              {t('profilesManager.changeLink', 'Cambiar link')}
            </button>
            <button
              onClick={() => { setOpenMenuId(null); handleToggleProfileStatus(profile.id, isActive); }}
              disabled={togglingStatus === profile.id}
              className={`pm-kebab-item ${isActive ? 'warn' : 'success'}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.36 6.64a9 9 0 11-12.73 0M12 2v10" />
              </svg>
              {isActive ? t('profilesManager.deactivate', 'Desactivar') : t('profilesManager.activate', 'Activar')}
            </button>
            <button
              onClick={() => { setOpenMenuId(null); handleDeleteProfile(profile); }}
              disabled={deletingProfile === profile.id}
              className="pm-kebab-item danger"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
              </svg>
              {t('profilesManager.deleteProfile', 'Eliminar perfil')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full dashboard-container p-6 pm-wrap">
      <style>{CSS}</style>
      <style>{SKELETON_CSS}</style>
      {/* Header */}
      <div className="mb-6 pm-header">
        <div>
          <p className="pm-greeting">{getGreeting()}, {user.name}</p>
          <h1 className="pm-title text-3xl">{t('profilesManager.home', 'Inicio')}</h1>
          <p className="pm-subtitle mt-2 text-sm">
            {t('profilesManager.manageAll', 'Gestiona todos tus perfiles digitales ({count}/{max})').replace('{count}', profiles.length).replace('{max}', getUserMaxProfiles())}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <NotificationBell setActiveTab={setActiveTab} />
          {canCreateMore ? (
            <button
              onClick={handleCreateProfile}
              disabled={creating}
              data-tutorial="create-profile"
              className="pm-btn pm-btn-primary"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {creating ? t('profilesManager.creating', 'Creando...') : t('profilesManager.createProfile', 'Crear Perfil')}
            </button>
          ) : (
            <button
              onClick={() => openUpgrade('multiple_profiles')}
              className="pm-btn"
              style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626' }}
              title={t('profilesManager.limitReachedTitle', `Llegaste al límite de ${getUserMaxProfiles()} perfiles de tu plan (los desactivados también cuentan)`).replace('{max}', getUserMaxProfiles())}
            >
              {t('profilesManager.limitReached', 'Límite de perfiles alcanzado · Actualizar plan')}
            </button>
          )}
        </div>
      </div>

      {/* Overview grid — real card treatment (matches Analytics' metric cards), no charts */}
      <div className="pm-stat-grid">
        <div className="pm-stat-card">
          <div className="pm-stat-icon" style={{background:'rgba(10,10,10,0.06)',color:'#0A0A0A'}}>
            <Users className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="pm-stat-value">{profiles.filter((p) => p.is_active !== false).length}</div>
          <div className="pm-stat-label">{t('profilesManager.stat.activeProfiles', 'Perfiles activos')}</div>
        </div>
        {hasFeature('analytics') && (
          <div className="pm-stat-card">
            <div className="pm-stat-icon" style={{background:'rgba(10,10,10,0.06)',color:'#0A0A0A'}}>
              <Eye className="w-4 h-4" strokeWidth={2} />
            </div>
            {homeStats.totalVisits !== null ? (
              <div className="pm-stat-value pm-stat-value-in">{homeStats.totalVisits}</div>
            ) : (
              <Skeleton width={40} height={26} radius={6} />
            )}
            <div className="pm-stat-label">{t('profilesManager.stat.totalVisits', 'Visitas totales')}</div>
          </div>
        )}
        {hasFeature('analytics') && (
          <div className="pm-stat-card">
            <div className="pm-stat-icon" style={{background:'rgba(10,10,10,0.06)',color:'#0A0A0A'}}>
              <MousePointerClick className="w-4 h-4" strokeWidth={2} />
            </div>
            {homeStats.totalClicks !== null ? (
              <div className="pm-stat-value pm-stat-value-in">{homeStats.totalClicks}</div>
            ) : (
              <Skeleton width={40} height={26} radius={6} />
            )}
            <div className="pm-stat-label">{t('profilesManager.stat.totalClicks', 'Clics totales')}</div>
          </div>
        )}
        {hasFeature('leads') && (
          <div className="pm-stat-card">
            <div className="pm-stat-icon" style={{background:'rgba(22,163,74,0.1)',color:'#16A34A'}}>
              <Target className="w-4 h-4" strokeWidth={2} />
            </div>
            {homeStats.leadsLast30 !== null ? (
              <div className="pm-stat-value pm-stat-value-in">{homeStats.leadsLast30}</div>
            ) : (
              <Skeleton width={40} height={26} radius={6} />
            )}
            <div className="pm-stat-label">{t('profilesManager.stat.leads30d', 'Leads (30 días)')}</div>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-6 pm-banner ${message.includes(t('profilesManager.error', 'Error')) ? 'pm-banner--error' : 'pm-banner--ok'}`}>
          {message}
        </div>
      )}

      {(!isMobile || profiles.length === 0) && (
      <div className="pm-table-card">
        {profiles.length === 0 ? (
          <div className="pm-empty">
            <div className="pm-empty-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
              </svg>
            </div>
            <p className="pm-empty-title">{t('profilesManager.empty.title', 'Aún no tienes perfiles')}</p>
            <p className="pm-empty-sub">{t('profilesManager.empty.sub', 'Crea tu primer perfil digital para empezar a compartir tu información')}</p>
          </div>
        ) : (
          <table className="pm-table">
            <thead>
              <tr>
                <th>{t('profilesManager.table.profiles', 'Perfiles')}</th>
                <th>URL</th>
                <th>{t('profilesManager.table.visits', 'Visitas')}</th>
                <th>{t('profilesManager.table.status', 'Estado')}</th>
                <th>{t('profilesManager.table.updated', 'Actualizado')}</th>
                <th style={{ textAlign: 'right' }}>{t('profilesManager.table.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile, idx) => {
                const hasImage = profile.profile_image_url || profile.profile_image_base64 || profile.profile_image;
                const isActive = profile.is_active !== false;
                const openUp = idx >= profiles.length - 2;
                return (
                  <tr
                    key={profile.id}
                    className={`pm-row ${currentProfile?.id === profile.id ? 'selected' : ''}`}
                    onClick={() => setCurrentProfile(profile)}
                  >
                    <td>
                      <div className="pm-profile-cell">
                        {hasImage ? (
                          <img
                            src={profile.profile_image_url
                              ? profile.profile_image_url
                              : profile.profile_image_base64
                              ? `data:${profile.profile_image_type || 'image/jpeg'};base64,${profile.profile_image_base64}`
                              : `${API}/files/${profile.profile_image}`}
                            alt={profile.name}
                            className="pm-avatar-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {!hasImage && (
                          <div className="pm-avatar-sm-fallback" style={{background: getAvatarColor(profile.id)}}>
                            {getInitials(profile)}
                          </div>
                        )}
                        <div>
                          <div className="pm-p-name pm-p-name-row">
                            <span>{profile.name} {profile.lastname}</span>
                            {profile.is_default && <span className="pm-default-badge">{t('profilesManager.mainBadge', 'Principal')}</span>}
                          </div>
                          <div className="pm-p-role">{profile.position || t('profilesManager.noPosition', 'Sin cargo')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="pm-url">/pages/{profile.slug}</td>
                    <td className="pm-visits">{profileVisits[profile.id] ?? profile.visits}</td>
                    <td>
                      <span className={`pm-badge ${isActive ? 'pm-badge--active' : 'pm-badge--inactive'}`}>
                        <span className="pm-badge-dot" />
                        {isActive ? t('profilesManager.active', 'Activo') : t('profilesManager.inactive', 'Inactivo')}
                      </span>
                    </td>
                    <td className="pm-date">{new Date(profile.updated_at).toLocaleDateString(dateLocale)}</td>
                    <td>
                      <ProfileActions profile={profile} isActive={isActive} openUp={openUp} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Mobile card list — same data as the table, laid out to avoid horizontal scroll */}
      {isMobile && profiles.length > 0 && (
        <div className="pm-mobile-list">
          {profiles.map((profile, idx) => {
            const hasImage = profile.profile_image_url || profile.profile_image_base64 || profile.profile_image;
            const isActive = profile.is_active !== false;
            const openUp = idx >= profiles.length - 2;
            const imgSrc = profile.profile_image_url
              ? profile.profile_image_url
              : profile.profile_image_base64
              ? `data:${profile.profile_image_type || 'image/jpeg'};base64,${profile.profile_image_base64}`
              : profile.profile_image
              ? `${API}/files/${profile.profile_image}`
              : null;
            return (
              <div
                key={profile.id}
                className={`pm-mobile-card ${currentProfile?.id === profile.id ? 'selected' : ''}`}
                onClick={() => setCurrentProfile(profile)}
              >
                <div className="pm-mobile-card-top">
                  <div className="pm-mobile-card-id">
                    {imgSrc ? (
                      <img src={imgSrc} alt={profile.name} className="pm-avatar-sm" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="pm-avatar-sm-fallback" style={{ background: getAvatarColor(profile.id) }}>
                        {getInitials(profile)}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="pm-mobile-card-name">
                        {profile.name} {profile.lastname}
                        {profile.is_default && <span className="pm-default-badge" style={{ marginLeft: 6 }}>{t('profilesManager.mainBadge', 'Principal')}</span>}
                      </div>
                      <div className="pm-mobile-card-role">{profile.position || t('profilesManager.noPosition', 'Sin cargo')}</div>
                    </div>
                  </div>
                  <span className={`pm-badge ${isActive ? 'pm-badge--active' : 'pm-badge--inactive'}`}>
                    <span className="pm-badge-dot" />
                    {isActive ? t('profilesManager.active', 'Activo') : t('profilesManager.inactive', 'Inactivo')}
                  </span>
                </div>
                <div className="pm-mobile-card-meta">
                  <span>/pages/{profile.slug}</span>
                  <span>·</span>
                  <span>{t('profilesManager.visitsCount', `${profileVisits[profile.id] ?? profile.visits} visitas`).replace('{n}', profileVisits[profile.id] ?? profile.visits)}</span>
                  <span>·</span>
                  <span>{new Date(profile.updated_at).toLocaleDateString(dateLocale)}</span>
                </div>
                <ProfileActions profile={profile} isActive={isActive} openUp={openUp} />
              </div>
            );
          })}
        </div>
      )}

      {/* Share Modal */}
      {shareModalProfile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeShareModal}
        >
          <div
            className="relative max-w-sm w-full animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeShareModal}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 hover:scale-110 transition-all z-10"
              style={{ border: '1px solid #E5E5E5' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden" style={{ border: '1px solid #E5E5E5' }}>

              {/* Top Label */}
              <div className="pm-modal-label">{t('profilesManager.share', 'Compartir')}</div>

              {/* QR Code Container */}
              <div className="pm-qr-box">
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    <QRCodeSVG
                      value={`${window.location.origin}/pages/${encodeURIComponent(shareModalProfile.slug)}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                      fgColor="#0A0A0A"
                      bgColor="#FFFFFF"
                    />
                  </div>

                  {/* Profile Handle */}
                  <div className="text-center">
                    <p className="pm-handle">
                      {shareModalProfile.name} {shareModalProfile.lastname}
                    </p>
                    <p className="pm-handle-sub">
                      @{shareModalProfile.slug}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pm-modal-actions">
                {/* Share Button */}
                <button
                  onClick={() => {
                    // Native share API if available
                    if (navigator.share) {
                      navigator.share({
                        title: t('profilesManager.shareTitle', `Perfil de ${shareModalProfile.name}`).replace('{name}', shareModalProfile.name),
                        text: t('profilesManager.shareText', 'Mira mi perfil en CONTACT'),
                        url: `${window.location.origin}/pages/${encodeURIComponent(shareModalProfile.slug)}`
                      }).catch(() => {});
                    } else {
                      handleCopyLink();
                    }
                  }}
                  className="pm-modal-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span>{t('profilesManager.share', 'Compartir')}</span>
                </button>

                {/* Copy Link Button */}
                <button
                  onClick={handleCopyLink}
                  className={`pm-modal-btn ${copySuccess ? 'pm-modal-btn--ok' : ''}`}
                >
                  {copySuccess ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{t('profilesManager.copied', '¡Copiado!')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>{t('profilesManager.copy', 'Copiar')}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Apple Wallet — usa el mismo link público que el QR de arriba.
                  Badge oficial cuando esté disponible; si no, estado "Próximamente"
                  (cubre tanto falta de credenciales de Apple como falta del asset). */}
              <hr className="pm-wallet-divider" />
              <div className="pm-wallet-row">
                <div className="pm-wallet-copy">
                  <div className="pm-wallet-title">{t('profilesManager.wallet.title', 'Agregar a Apple Wallet')}</div>
                  <div className="pm-wallet-sub">{t('profilesManager.wallet.sub', 'Lleva tu tarjeta CONTACT en tu iPhone, lista para compartir.')}</div>
                </div>
                {walletConfigured && !walletBadgeError ? (
                  <a
                    className="pm-wallet-badge-link"
                    href={`${API}/profile/${encodeURIComponent(shareModalProfile.slug)}/apple-wallet.pkpass`}
                  >
                    <img
                      className="pm-wallet-badge-img"
                      src="/apple-wallet-badge.svg"
                      alt={t('profilesManager.wallet.title', 'Agregar a Apple Wallet')}
                      onError={() => setWalletBadgeError(true)}
                    />
                  </a>
                ) : (
                  <span className="pm-wallet-soon">{t('profilesManager.comingSoon', 'Próximamente')}</span>
                )}
              </div>

              {/* Google Wallet — mismo link público, mismo patrón que Apple
                  Wallet arriba. Google no genera un archivo: el endpoint
                  redirige directo a la URL firmada de pay.google.com. */}
              <div className="pm-wallet-row">
                <div className="pm-wallet-copy">
                  <div className="pm-wallet-title">{t('profilesManager.googleWallet.title', 'Agregar a Google Wallet')}</div>
                  <div className="pm-wallet-sub">{t('profilesManager.googleWallet.sub', 'Lleva tu tarjeta CONTACT en tu Android, lista para compartir.')}</div>
                </div>
                {googleWalletConfigured && !googleWalletBadgeError ? (
                  <a
                    className="pm-wallet-badge-link"
                    href={`${API}/profile/${encodeURIComponent(shareModalProfile.slug)}/google-wallet`}
                  >
                    <img
                      className="pm-wallet-badge-img"
                      src="/google-wallet-badge.svg"
                      alt={t('profilesManager.googleWallet.title', 'Agregar a Google Wallet')}
                      onError={() => setGoogleWalletBadgeError(true)}
                    />
                  </a>
                ) : (
                  <span className="pm-wallet-soon">{t('profilesManager.comingSoon', 'Próximamente')}</span>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Slug Change Modal */}
      {slugModalProfile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeSlugModal}
        >
          <div
            className="relative max-w-sm w-full animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeSlugModal}
              disabled={savingSlug}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 hover:scale-110 transition-all z-10"
              style={{ border: '1px solid #E5E5E5' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden" style={{ border: '1px solid #E5E5E5' }}>

              <div className="pm-modal-label">{t('profilesManager.slug.changeLink', 'Cambiar Link')}</div>

              <div className="pm-slug-warning">
                {t('profilesManager.slug.warning', 'Al cambiar el link, cualquier tarjeta, QR o enlace que ya compartiste con el link actual dejará de funcionar, y el link anterior queda libre para que otra persona lo use.')}
              </div>

              <label className="pm-form-label">{t('profilesManager.slug.newLink', 'Nuevo link')}</label>
              <div className="pm-slug-input-row">
                <span className="pm-slug-prefix">/pages/</span>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => { setSlugInput(e.target.value); setSlugError(''); }}
                  className="pm-slug-input"
                  placeholder={t('profilesManager.slug.placeholder', 'tu-nombre')}
                  disabled={savingSlug}
                  autoFocus
                />
              </div>
              {slugError && <p className="pm-slug-error">{slugError}</p>}

              <div className="pm-modal-actions" style={{ marginTop: 18 }}>
                <button onClick={closeSlugModal} disabled={savingSlug} className="pm-modal-btn">
                  {t('profilesManager.cancel', 'Cancelar')}
                </button>
                <button
                  onClick={handleSaveSlug}
                  disabled={savingSlug || !slugInput.trim() || slugInput.trim() === slugModalProfile.slug}
                  className="pm-modal-btn pm-modal-btn--primary"
                >
                  {savingSlug ? t('profilesManager.saving', 'Guardando...') : t('profilesManager.saveChanges', 'Guardar cambios')}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilesManager;
